import styled from '@emotion/styled'
import { Upload } from 'antd'
import { RcFile, UploadChangeParam } from 'antd/lib/upload'
import { useCallback, useEffect, useState } from 'react'
// import { useTranslation } from 'react-i18next'
import color from '../../constant/colors'
import { useVisibility } from '../../utils/custom-hook'
import { mobile, useScreen } from '../../utils/responsive-helper'
import Button from './Button'
import Image, { ImageNameKeys } from './Image'
import Modal from './Modal'
import OutlineButton from './OutlineButton'
import Text from './Text'
import config from '../../config'

const Layout = styled.div`
  background-color: WHITE;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  border: solid 1px ${color.DIM_GRAY};
`

const ImagePreviewPanel = styled.div<{ isMobile: boolean }>`
  padding: 1px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 100%;
  height: 100%;
  column-gap: 12px;
`

const FileIconContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const CommentImage = styled.img<{ isNewUpload?: boolean }>`
  max-width: 50%;
  border: 0px solid black;
  object-fit: contain;   
  ${props => props.isNewUpload ? '' : 'cursor: pointer;'}

  ${mobile} {
    object-fit: cover;
  }
`

// const LayoutRowButtonUpload = styled.div``

const LayoutModal = styled.div`
  margin-top: 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const LayoutButtonModal = styled(Button)`
  width: 300px;
  margin: 50px 0 0 0;
`
const LayoutBodyColumn = styled.div`
  padding: 8px;

  ${mobile} {
    padding: 12px;
  }
`

const LayoutBodyRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-bottom: 16px;
`

const LayoutTitleRow = styled.div`
  flex: 1;
  display: 'flex';
  flex-direction: 'column';
`
const LayoutBackgroundFooter = styled.div`
  padding: 8px;
`
const BackgroundUpload = styled.div`
  background: #ECECEC;
  height: 150px;
  width: 100%;
  border-radius: 8px;
`

export type UploadFileProps = {
  icon?: ImageNameKeys
  title: string
  desc: string
  value?: string
  onChange?: (v: any) => void
  isDelete?: boolean
  disabled?: boolean
  /**
   * maximumFileSize in MB, default 2 MB
   * */
  maximumFileSize?: number
  isPreview?: boolean
}

const UploadFile = (props: UploadFileProps) => {
  const {
    title,
    desc,
    onChange,
    value,
    isDelete = false,
    maximumFileSize = 3,
    disabled = false,
  } = props
  // const { t } = useTranslation()
  const [selectedFile, setSelectedFile] = useState<any>(value || '')
  const [selectedFileName, setSelectedFileName] = useState('')
  const [preview, setPreview] = useState('')
  const { isMobile } = useScreen()
  const [modalVisible, modalAction] = useVisibility()
  const [errorMessage, setErrorMessage] = useState('')
  // const [isFile, setIsFile] = useState(false)

  useEffect(() => {

    if (!selectedFile) {
      setPreview('')
      return
    }

    // Try to extract filename from data URI if present (e.g., data:application/pdf;name=foo.pdf;base64,...)
    if (
      typeof selectedFile === 'string' &&
      selectedFile.startsWith('data:') &&
      !selectedFileName
    ) {
      const match = selectedFile.match(/;name=([^;]+);base64,/i)
      if (match && match[1]) {
        try {
          setSelectedFileName(decodeURIComponent(match[1]))
        } catch {
          setSelectedFileName(match[1])
        }
      }
    }

    // When value is a data URI (base64 of any type), use it directly for preview
    // Otherwise, treat it as a server path and prepend apiHost
    if (typeof selectedFile === 'string' && selectedFile.startsWith('data:')) {
      setPreview(selectedFile)
    } else {
      setPreview(`${config.apiHost}/files${selectedFile}`)
    }

    // if (!selectedFile.includes('data:image') && selectedFileName === '' && selectedFile.substr(0, 8) !== 'https://') {
    //   setSelectedFileName(`file: ${selectedFile.split('/')[1].split(';')[0]}`)
    // }

    // Determine if the current value represents a downloadable file from API (pdf/zip path)
    // const isDownloadable = ['pdf', 'zip'].includes(selectedFile.split('.').pop())
    // setIsFile(isDownloadable)

  }, [selectedFile, selectedFileName])

  const onRemoveClick = useCallback(
    (e) => {
      if (!disabled) {
        e.preventDefault()
        setPreview('')
        setSelectedFile('')
        onChange?.('')
      }
    },
    [disabled, onChange],
  )

  const getBase64 = useCallback(async (file: any) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = (error) => reject(error)
    })
  }, [])

  const onSelectedFile = useCallback(
    async (params: UploadChangeParam) => {
      const { file: image } = params
      const isLarge = image.size / 1024 / 1024 > maximumFileSize

      if (!isLarge) {
        if (image) {
          const imageBase64 = (await getBase64(image)) as string
          // For non-image files (e.g., PDF/ZIP), embed original filename into data URI so it persists in store
          if (!image.type.includes('image')) {
            const encodedName = encodeURIComponent(image?.name || '')
            const withName = imageBase64.replace(';base64,', `;name=${encodedName};base64,`)
            onChange?.(withName)
            setSelectedFile(withName)
            setSelectedFileName(image?.name)
          } else {
            onChange?.(imageBase64)
            setSelectedFile(imageBase64)
          }
        }
      }
    },
    [getBase64, onChange, maximumFileSize],
  )

  const validateUploadFile = useCallback(
    (image: RcFile) => {
      const isLarge = image.size / 1024 / 1024 > maximumFileSize
      if (isLarge) {
        modalAction.show()
        setPreview('')
        setSelectedFile('')
        onChange?.('')
        setErrorMessage('ขนาด ภาพ/ไฟล์ ใหญ่เกินไป')
      }
    },
    [maximumFileSize, modalAction, onChange],
  )

  const beforeUpload = useCallback(
    (image: RcFile) => {
      validateUploadFile(image)
      return false
    },
    [validateUploadFile],
  )

  return (
    <Layout className="layout-upload">
      <Modal visible={modalVisible} onCancel={modalAction.hide} hideFooter>
        <LayoutModal>
          <Text>{errorMessage}</Text>
          <LayoutButtonModal onClick={modalAction.hide}>Cancel</LayoutButtonModal>
        </LayoutModal>
      </Modal>
      <LayoutBodyColumn>
        <LayoutBodyRow>
          {/* <Image imageName={icon} width={48} height={48} /> */}
          <LayoutTitleRow>
            <Text size={18} type={'Medium'}>
              {title}
            </Text>
            <Text size={14} type={'Medium'}>
              {desc}
            </Text>
            <Text size={14} type={'Medium'} color={color.FONT_LIGHT_GRAY}>
              (jpg, jpeg, png, pdf, zip ขนาดไม่เกิน 2 MB)
            </Text>
          </LayoutTitleRow>
          {!selectedFile && (
            <Upload
              action=""
              listType="text"
              accept=".jpg,.jpeg,.png,.pdf,.zip"
              onChange={onSelectedFile}
              style={{ width: '100% ' }}
              beforeUpload={beforeUpload}
              showUploadList={false}
              disabled={disabled}
            >
              <OutlineButton disabled={disabled} style={{ width: 'fit-content' }}>+ เพิ่มไฟล์</OutlineButton>
            </Upload>
          )}
        </LayoutBodyRow>
      </LayoutBodyColumn>
      <LayoutBackgroundFooter>
        <BackgroundUpload>
          {selectedFile && (
            <ImagePreviewPanel isMobile={isMobile}>
              {(() => {
                const valueString = String(selectedFile || '')
                const lowerValue = valueString.toLowerCase()
                const isDataUri = lowerValue.startsWith('data:')
                const isBase64Image = isDataUri && lowerValue.startsWith('data:image')
                const isBase64Pdf = isDataUri && lowerValue.startsWith('data:application/pdf')
                const isBase64Zip = isDataUri && (lowerValue.startsWith('data:application/zip') || lowerValue.startsWith('data:application/x-zip-compressed'))
                const isPathImage = /\.(jpg|jpeg|png)$/.test(lowerValue)
                const isPathPdfOrZip = /\.(pdf|zip)$/.test(lowerValue)

                // Downloadable files coming from API paths (pdf/zip)
                if (isPathPdfOrZip) {
                  return (
                    <FileIconContainer>
                      <Image imageName={'iconPDF'} height={50} />
                      <a href={preview} target="_blank" rel="noopener noreferrer">Click to download File</a>
                    </FileIconContainer>
                  )
                }

                // Images (either base64 data URL or API path)
                if (isBase64Image || isPathImage) {
                  return (
                    <CommentImage
                      src={preview}
                      alt=""
                      isNewUpload={isBase64Image}
                      onClick={() => {
                        if (preview && !isDataUri) {
                          window.open(preview, '_blank')
                        }
                      }}
                    />
                  )
                }

                // Base64 PDFs or ZIPs uploaded via UI: show icon and file name
                if (isBase64Pdf || isBase64Zip) {
                  const isPdf = isBase64Pdf
                  const fallbackName = isPdf ? 'PDF file' : 'ZIP file'
                  let displayName = selectedFileName
                  if (!displayName && isDataUri) {
                    const nameMatch = valueString.match(/;name=([^;]+);base64,/i)
                    if (nameMatch && nameMatch[1]) {
                      try {
                        displayName = decodeURIComponent(nameMatch[1])
                      } catch {
                        displayName = nameMatch[1]
                      }
                    }
                  }
                  return (
                    <FileIconContainer>
                      <Image imageName={'iconPDF'} height={50} />
                      <Text size={14} type={'Medium'}>
                        {displayName || fallbackName}
                      </Text>
                    </FileIconContainer>
                  )
                }

                // Default: nothing
                return null
              })()}
              {/* {isFile ?
                <div>
                  <a href={preview} target="_blank" rel="noopener noreferrer">
                    Click to download File
                  </a>
                </div> :
                <CommentImage src={preview} alt="" />
              } */}
              {isDelete === false && (
                <Image imageName='closeIcon18' width={18} height={18} onClick={onRemoveClick} style={{ cursor: 'pointer' }} />
              )}
            </ImagePreviewPanel>
          )}
        </BackgroundUpload>
      </LayoutBackgroundFooter>
    </Layout>
  )
}
export default UploadFile
